import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EMAIL_WARNING_DISMISSED_KEY } from '@client/hooks/useClientInvite';

import _ from 'lodash';
import {
  selectSessionAdvisor,
  resetClientEmailState,
  selectClientEmailState,
  postIntakeFormEmailRequest,
  postBatchIntakeFormEmailRequest,
} from '@client/model';
import { v4 as newUuid } from 'uuid';
import { analytics } from '@client/analytics';
import { getIntroIntakeFormApiEndPoint, isValidEmailFormat } from '@shared';

export type StepType =
  | 'method'
  | 'email'
  | 'customize-email'
  | 'email-additional-settings'
  | 'link'
  | 'link-additional-settings';

type ShareResultsOption = 'yes' | 'no';

export interface Tag {
  value: string;
  isValid: boolean;
  error: string;
}

export interface InviteClientConfig {
  currentStep: StepType;
  email: string;
  firstName: string;
  lastName: string;
  isBulkMode: boolean;
  bulkEmails: Tag[];
  isComplianceConfirmed: boolean;
  emailBody: string;
  shareResults: ShareResultsOption;
  referralSource?: string | undefined;
  clientRelationship?: ClientRelationshipStage | undefined;
}

export interface InviteClientContextType {
  inviteClientConfig: InviteClientConfig;
  showEmailWarningModal: boolean;
  isBulkInvite: boolean;
  isSingleInvite: boolean;
  isEmailValid: boolean;
  canContinueSingle: boolean;
  validBulkEmails: Tag[];
  canContinueBulk: boolean;
  canContinue: boolean;
  isLoading: boolean;
  isComplete: boolean;
  isTestEmailLoading: boolean;
  isTestEmailComplete: boolean;
  inviteCount: number;

  setInviteClientConfig: Dispatch<SetStateAction<InviteClientConfig>>;
  setShowEmailWarningModal: Dispatch<SetStateAction<boolean>>;

  handleConnectEmail: () => void;
  generateIntakeFormLink: (surveyId: string) => string;
  generateEmailText: () => string;
  sendInvites: () => void;
  handleSendEmailAnyway: () => void;
  handleStepChange: (step: StepType) => void;
  toggleBulkMode: () => void;
  handleSendEmail: () => void;
  handleSendTestEmail: () => void;
  generateShareableLink: () => string;
  handleConfigChange: (
    field: keyof InviteClientConfig,
    value: string | boolean | Tag[] | undefined,
  ) => void;

  referralSources?: AdvisorReferralSource[];
  setReferralSources?: Dispatch<SetStateAction<AdvisorReferralSource[]>>;
}

const DEFAULT_EMAIL_BODY = `As one of the most devastating events in retirement, it means a lot to me to prepare my clients for the possibility of a long-term care event.

I use Waterlily to build personalized long-term care plans for clients. It works by predicting your unique future LTC needs, building a care plan for you, and then I can analyze different ways we could fund these costs to find the options that make the most sense for you.

The first step is to complete the intake form below (it's about 5 minutes long) and then we get the predictions that we can work with and build from there.`;

const InviteClientContext = createContext<InviteClientContextType | null>(null);

export function useInviteClientContext() {
  const context = useContext(InviteClientContext);

  if (!context) {
    throw new Error(
      '`useInviteClientContext` should we used inside the `<InviteClientProvider />`',
    );
  }

  return context;
}

export function useInviteClientTrigger() {
  const [searchParams, setSearchParams] = useSearchParams();

  const isInviteClientOpen = searchParams.get('inviteClient') === 'true';
  const inviteClientType = searchParams.get('inviteClientType') as StepType;

  const setInviteClientParams = useCallback(
    (type?: StepType) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);

        newParams.set('inviteClient', 'true');
        newParams.set('inviteClientType', type || 'method');

        return newParams;
      });
    },
    [setSearchParams],
  );

  const clearInviteClientParams = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      newParams.delete('inviteClient');
      newParams.delete('inviteClientType');

      return newParams;
    });
  }, [setSearchParams]);

  return {
    isInviteClientOpen,
    inviteClientType,
    setInviteClientParams,
    clearInviteClientParams,
  };
}

export function InviteClientProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const { status: clientEmailStatus, failedEmails } = useSelector(selectClientEmailState);
  const { isInviteClientOpen, inviteClientType, clearInviteClientParams } =
    useInviteClientTrigger();

  const [showEmailWarningModal, setShowEmailWarningModal] = useState(false);

  const [referralSources, setReferralSources] = useState<AdvisorReferralSource[]>(
    _.sortBy(
      advisor.referralSources?.map(source => ({
        referralSourceLabel: source.referralSourceLabel,
        advisorReferralSourceId: source.advisorReferralSourceId,
      })),
      source => source.referralSourceLabel.toLowerCase().trim(),
    ) ?? [],
  );

  const defaultInviteClientConfig: InviteClientConfig = {
    currentStep: inviteClientType || 'method',
    email: '',
    firstName: '',
    lastName: '',
    isBulkMode: false,
    bulkEmails: [],
    isComplianceConfirmed: false,
    emailBody: DEFAULT_EMAIL_BODY,
    shareResults: advisor.sendClientResultsLinkSetting ? 'yes' : 'no',
  };

  const [inviteClientConfig, setInviteClientConfig] =
    useState<InviteClientConfig>(defaultInviteClientConfig);

  const statusBulk = clientEmailStatus.batchIntakeForm;
  const statusSingle = clientEmailStatus.individualIntakeForm;
  const statusTestEmail = clientEmailStatus.testEmail;

  const isEmailValid = isValidEmailFormat(inviteClientConfig.email);
  const canContinueSingle = inviteClientConfig.email.length > 0 && isEmailValid;
  const validBulkEmails = inviteClientConfig.bulkEmails.filter(email => email.isValid);
  const canContinueBulk = validBulkEmails.length > 0;
  const canContinue = inviteClientConfig.isBulkMode ? canContinueBulk : canContinueSingle;

  const isBulkInvite = inviteClientConfig.isBulkMode;
  const isSingleInvite = !inviteClientConfig.isBulkMode;
  const inviteCount = inviteClientConfig.isBulkMode ? validBulkEmails.length : 1;

  const isLoading = statusBulk === 'loading' || statusSingle === 'loading';
  const isComplete = statusBulk === 'complete' || statusSingle === 'complete';
  const isTestEmailLoading = statusTestEmail === 'loading';
  const isTestEmailComplete = statusTestEmail === 'complete';

  const hasConnectedEmail = !!(
    (advisor.advisorOauthSettings?.google?.userEmail &&
      advisor.advisorOauthSettings?.google?.createdAtDateTime) ||
    (advisor.advisorOauthSettings?.microsoft?.userEmail &&
      advisor.advisorOauthSettings?.microsoft?.createdAtDateTime)
  );

  useEffect(() => {
    if (isInviteClientOpen) {
      resetState();
    }
  }, [isInviteClientOpen]);

  useEffect(() => {
    if (isComplete) {
      handleClose();
    }
  }, [isComplete]);

  // Handle failed emails in bulk mode
  useEffect(() => {
    if (statusBulk === 'completeWithErrors' && failedEmails?.length) {
      setInviteClientConfig(currentConfig => ({
        ...currentConfig,
        bulkEmails: currentConfig.bulkEmails.filter(el => failedEmails.includes(el.value)),
      }));
    }
  }, [statusBulk, failedEmails]);

  useEffect(() => {
    const { firstName } = inviteClientConfig;

    const greeting = firstName ? `Hi ${firstName},\n\n` : '';

    const emailText = `${greeting}As one of the most devastating events in retirement, it means a lot to me to prepare my clients for the possibility of a long-term care event.

I use Waterlily to build personalized long-term care plans for clients. It works by predicting your unique future LTC needs, building a care plan for you, and then I can analyze different ways we could fund these costs to find the options that make the most sense for you.

The first step is to complete the intake form below (it's about 5 minutes long) and then we get the predictions that we can work with and build from there.`;

    setInviteClientConfig(currentConfig => ({
      ...currentConfig,
      emailBody: emailText,
    }));
  }, [inviteClientConfig.firstName]);

  function resetState() {
    setInviteClientConfig(defaultInviteClientConfig);
    dispatch(resetClientEmailState());
  }

  function handleClose() {
    resetState();
    dispatch({ type: 'RESUME_SESSION_REFRESH' });
    clearInviteClientParams();
  }

  const handleConnectEmail = useCallback(() => {
    setShowEmailWarningModal(false);
    handleClose();
    navigate(`/advisors/${advisor.advisorId}/settings?tab=communication`);
  }, [advisor.advisorId]);

  const generateIntakeFormLink = useCallback(
    (surveyId: string) => {
      return getIntroIntakeFormApiEndPoint(
        advisor.advisorId,
        inviteClientConfig.shareResults === 'yes',
        surveyId,
      );
    },
    [advisor.advisorId, inviteClientConfig.shareResults],
  );

  const generateEmailText = useCallback(() => {
    const surveyId = newUuid();
    const intakeFormLink = generateIntakeFormLink(surveyId);

    let emailText = inviteClientConfig.emailBody || DEFAULT_EMAIL_BODY;

    emailText += `\n\n${intakeFormLink}`;
    emailText += `\n\nBest,\n${advisor.advisorFirstName}`;

    return emailText;
  }, [generateIntakeFormLink, inviteClientConfig.emailBody, advisor.advisorFirstName]);

  const handleStepChange = useCallback((step: StepType) => {
    setInviteClientConfig(currentConfig => ({ ...currentConfig, currentStep: step }));
  }, []);

  const toggleBulkMode = useCallback(() => {
    setInviteClientConfig(currentConfig => ({
      ...currentConfig,
      isBulkMode: !currentConfig.isBulkMode,
      ...(currentConfig.isBulkMode
        ? {
            bulkEmails: [],
          }
        : {
            email: '',
            firstName: '',
            lastName: '',
          }),
    }));
  }, []);

  function shouldShowEmailDeliverabilityWarning() {
    return localStorage.getItem(EMAIL_WARNING_DISMISSED_KEY) !== 'true';
  }

  const sendInvites = useCallback(() => {
    analytics.inviteClients({
      clientCount: inviteCount,
      inviteMethod: isSingleInvite ? 'single' : 'bulk',
    });

    if (isSingleInvite) {
      const surveyId = newUuid();

      dispatch(
        postIntakeFormEmailRequest({
          surveyId,
          createNewClient: true,
          advisorId: advisor.advisorId,
          email: inviteClientConfig.email.trim(),
          clientFirstName: inviteClientConfig.firstName.trim(),
          clientLastName: inviteClientConfig.lastName.trim(),
          advisorProvidedBody: inviteClientConfig.emailBody,
          intakeFormLink: generateIntakeFormLink(surveyId),
          clientRelationshipStage: inviteClientConfig.clientRelationship,
          referralSourceId: inviteClientConfig.referralSource,
          isTestEmail: false,
        }),
      );
    } else {
      if (!inviteClientConfig.isComplianceConfirmed) return;

      const payload = validBulkEmails.map(emailTag => {
        const surveyId = newUuid();
        return {
          email: emailTag.value.trim(),
          surveyId,
          advisorId: advisor.advisorId,
          intakeFormLink: generateIntakeFormLink(surveyId),
          clientRelationshipStage: inviteClientConfig.clientRelationship,
          referralSourceId: inviteClientConfig.referralSource,
          advisorProvidedBody: inviteClientConfig.emailBody,
        };
      });

      dispatch(postBatchIntakeFormEmailRequest({ intakeFormEmailProps: payload }));
    }
  }, [
    inviteCount,
    isSingleInvite,
    dispatch,
    advisor.advisorId,
    inviteClientConfig.email,
    inviteClientConfig.firstName,
    inviteClientConfig.lastName,
    inviteClientConfig.emailBody,
    inviteClientConfig.clientRelationship,
    inviteClientConfig.referralSource,
    inviteClientConfig.isComplianceConfirmed,
    generateIntakeFormLink,
    validBulkEmails,
  ]);

  const handleSendEmail = useCallback(() => {
    if (!hasConnectedEmail && shouldShowEmailDeliverabilityWarning()) {
      setShowEmailWarningModal(true);
      return;
    }
    sendInvites();
  }, [hasConnectedEmail, sendInvites]);

  const handleSendEmailAnyway = useCallback(() => {
    setShowEmailWarningModal(false);
    sendInvites();
  }, [sendInvites]);

  const handleSendTestEmail = useCallback(() => {
    dispatch(
      postIntakeFormEmailRequest({
        advisorId: advisor.advisorId,
        email: advisor.advisorEmail,
        intakeFormLink: getIntroIntakeFormApiEndPoint(
          advisor.advisorId,
          inviteClientConfig.shareResults === 'yes',
          newUuid(),
        ),
        isTestEmail: true,
        advisorProvidedBody: inviteClientConfig.emailBody,
      }),
    );
  }, [
    dispatch,
    advisor.advisorId,
    advisor.advisorEmail,
    inviteClientConfig.shareResults,
    inviteClientConfig.emailBody,
  ]);

  const handleConfigChange = useCallback(
    (
      field: keyof typeof inviteClientConfig,
      value: (typeof inviteClientConfig)[keyof typeof inviteClientConfig],
    ) => {
      setInviteClientConfig(currentConfig => ({ ...currentConfig, [field]: value }));
    },
    [],
  );

  const generateShareableLink = useCallback(() => {
    return getIntroIntakeFormApiEndPoint(
      advisor.advisorId,
      inviteClientConfig.shareResults === 'yes',
      undefined,
      inviteClientConfig.referralSource,
    );
  }, [advisor.advisorId, inviteClientConfig.shareResults, inviteClientConfig.referralSource]);

  const contextValue = {
    inviteClientConfig,
    showEmailWarningModal,
    isBulkInvite,
    isSingleInvite,
    isEmailValid,
    canContinueSingle,
    validBulkEmails,
    canContinueBulk,
    canContinue,
    isLoading,
    isComplete,
    isTestEmailLoading,
    isTestEmailComplete,
    inviteCount,

    setInviteClientConfig,
    setShowEmailWarningModal,

    handleConnectEmail,
    generateIntakeFormLink,
    generateEmailText,
    sendInvites,
    handleSendEmailAnyway,
    handleStepChange,
    toggleBulkMode,
    handleSendEmail,
    handleSendTestEmail,
    generateShareableLink,
    handleConfigChange,

    referralSources,
    setReferralSources,
  };

  return (
    <InviteClientContext.Provider value={contextValue}>{children}</InviteClientContext.Provider>
  );
}
